import React, { useState, useEffect, useMemo, useRef } from 'react'
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import LoadingCenter from './util/LoadingCenter';
import NoAccess from './util/NoAccess';
import { useLocation } from "react-router-dom";
import { t } from 'i18next';
import AddIcon from '@mui/icons-material/Add';
import AddExpenses from './modal/AddExpenses';
import SidePanel from './util/SidePanel';
import SettingsPanelBody from './util/SettingsPanelBody';
import CarExpensesSection from './expensesComponents/CarExpensesSection';
import FinanceExpensesTable from './expensesComponents/FinanceExpensesTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import Tooltip from '@mui/material/Tooltip';
import TabPanel from '@mui/lab/TabPanel';
import ExpensesSettings from './modal/ExpensesSettings';
import AddExpensesCar from './modal/AddExpensesCar';
import EditCarExpenses from './modal/EditCarExpenses';
import { useSnackbar } from 'notistack';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import EditExpenses from './modal/EditExpenses';
import FinanceOverview from './financeComponents/FinanceOverview';
import AddIncome from './modal/AddIncome';
import EditIncome from './modal/EditIncome';
import DataTable from './util/DataTable';
import { ColDef } from './util/DataTable';
import PayrollTable from './payrollComponents/PayrollTable';
import UserEditFinance from './modal/UserEditFinance';
import TabMenu from './util/TabMenu';
import PageToolbar from './util/PageToolbar';
import SearchFilter, { applySearchAndFilters, ActiveFilter } from './util/SearchFilter';

interface FinanceProps {
  access: any;
  is24HourFormat: any;
  staticDateCompany: any;
  baseUrl: any;
  unit: any;
}


const Finance: React.FC<FinanceProps> = ({ access, is24HourFormat, staticDateCompany, baseUrl, unit }) => {
  const auth = useSelector((state: RootState) => state.auth.value);
  const [tab, setTab] = useState('overview');
  const [showModalAdd, setShowModalAdd] = useState(false);
  const [showModalCarAdd, setShowModalCarAdd] = useState(false);
  const [showModalIncomeAdd, setShowModalIncomeAdd] = useState(false);
  const [expensesRow, setExpensesRow] = useState<any[]>([]);
  const [, setExpensesRowOriginal] = useState<any[]>([]);
  const [expensesRowCar, setExpensesRowCar] = useState<any[]>([]);
  const [, setExpensesRowOriginalCar] = useState<any[]>([]);
  const [incomeRow, setIncomeRow] = useState<any[]>([]);
  const [incomeRowOriginal, setIncomeRowOriginal] = useState<any[]>([]);
  const [incomeSearch, setIncomeSearch] = useState('');
  const [incomeFilters, setIncomeFilters] = useState<ActiveFilter[]>([]);
  const [cars, setCars] = useState<any[]>([]);
  const [fields, setFields] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingCar, setLoadingCar] = useState(false);
  const [loadingIncome, setLoadingIncome] = useState(false);
  const [loadingPayroll, setLoadingPayroll] = useState(false);
  const [payrollUsers, setPayrollUsers] = useState<any[]>([]);
  const [payrollOriginalUsers, setPayrollOriginalUsers] = useState<any[]>([]);
  const [contractType, setContractType] = useState<{ id: number; name: string }[]>([]);
  const [payrollFilters, setPayrollFilters] = useState<ActiveFilter[]>([]);
  const [selectedPayrollUser, setSelectedPayrollUser] = useState<any>(null);
  const [showPayrollEdit, setShowPayrollEdit] = useState(false);
  const [showCarEdit, setShowCarEdit] = useState(false);
  const [selectedCarExpense, setSelectedCarExpense] = useState<any>(null);
  const [showIncomeEdit, setShowIncomeEdit] = useState(false);
  const [selectedIncome, setSelectedIncome] = useState<any>(null);
  const [showEditSetting, setShowEditSetting] = useState(false);
  const [show, setShow] = useState(false);
  const [payrollSearch, setPayrollSearch] = useState('');
  const location = useLocation();
  const incomeToolbarSlot  = useRef<HTMLDivElement>(null);
  const payrollToolbarSlot = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  // Deep link from the dashboard Expenses card: /finance?add=expense | mileage
  const pendingAdd = useRef<string | null>(new URLSearchParams(location.search).get('add'));
  useEffect(() => {
    if (pendingAdd.current === 'expense') setTab('expenses');
    else if (pendingAdd.current === 'mileage') setTab('car');
  }, []);

  // Close inline forms when switching tabs (then open the deep-linked one)
  useEffect(() => {
    setShowModalAdd(false);
    setShowModalCarAdd(false);
    setShowModalIncomeAdd(false);
    if (pendingAdd.current === 'expense' && tab === 'expenses') { setShowModalAdd(true); pendingAdd.current = null; }
    if (pendingAdd.current === 'mileage' && tab === 'car') { setShowModalCarAdd(true); pendingAdd.current = null; }
  }, [tab]);

  const handleShow = (item: any) => { setSelectedUser(item); setShow(true); };

  // No date range: the lists come back whole (by role scope); the table filters narrow them
  const reportRequestData = () => ({});

  const loadPayroll = async () => {
    setLoadingPayroll(true);
    try {
      const [ctRes, usersRes] = await Promise.allSettled([
        axios.get('hr-schedule-template'),
        axios.get('users/finance'),
      ]);
      if (ctRes.status === 'fulfilled') setContractType(ctRes.value.data || []);
      if (usersRes.status === 'fulfilled') {
        setPayrollUsers(usersRes.value.data || []);
        setPayrollOriginalUsers(usersRes.value.data || []);
      }
    } finally {
      setLoadingPayroll(false);
    }
  };

  useEffect(() => {
    setLoading(true); setLoadingCar(true); setLoadingIncome(true);
    const load = async () => {
      try {
        const { data } = await axios.get('user');
        if (data) { /* auth managed by App.tsx */ }

        try { const { data: f } = await axios.get('fields'); setFields(f || []); } catch {}
        try { const { data: c } = await axios.get('cars'); setCars(c || []); } catch {}

        const rq = reportRequestData();
        try {
          const { data: e } = await axios.post('expenses', rq);
          setExpensesRow(e?.expenses || []); setExpensesRowOriginal(e?.expenses || []);
        } catch { setExpensesRow([]); setExpensesRowOriginal([]); }

        try {
          const { data: ce } = await axios.post('cars-expenses', rq);
          setExpensesRowCar(ce || []); setExpensesRowOriginalCar(ce || []);
        } catch { setExpensesRowCar([]); setExpensesRowOriginalCar([]); }

        try {
          const { data: inc } = await axios.post('finance-income', rq);
          setIncomeRow(inc?.income || []); setIncomeRowOriginal(inc?.income || []);
        } catch { setIncomeRow([]); setIncomeRowOriginal([]); }

      } catch { /* auth errors are handled by the global interceptor */ }
      finally { setLoading(false); setLoadingCar(false); setLoadingIncome(false); }
    };
    // без доступа к разделу запросы вернут 403 — не отправляем их вовсе
    if (access?.finance_enabled) {
      load();
      loadPayroll();
    } else {
      setLoading(false); setLoadingCar(false); setLoadingIncome(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, location.pathname, access?.finance_enabled]);

  const refreshExpenses = async () => {
    setLoading(true); setLoadingCar(true); setLoadingIncome(true);
    const rq = reportRequestData();
    try {
      const [expRes, carRes, incRes] = await Promise.allSettled([
        axios.post('expenses', rq),
        axios.post('cars-expenses', rq),
        axios.post('finance-income', rq),
      ]);
      if (expRes.status === 'fulfilled') { setExpensesRow(expRes.value.data?.expenses || []); setExpensesRowOriginal(expRes.value.data?.expenses || []); }
      if (carRes.status === 'fulfilled') { setExpensesRowCar(carRes.value.data || []); setExpensesRowOriginalCar(carRes.value.data || []); }
      if (incRes.status === 'fulfilled') { setIncomeRow(incRes.value.data?.income || []); setIncomeRowOriginal(incRes.value.data?.income || []); }
    } finally { setLoading(false); setLoadingCar(false); setLoadingIncome(false); }
  };

  const refreshCars = async () => { try { const { data } = await axios.get('cars'); setCars(data); } catch {} };
  const refreshFields = async () => { try { const { data } = await axios.get('fields'); setFields(data); } catch {} };

  const incomeUserOptions = useMemo(() => {
    const seen = new Set<string>();
    return incomeRow
      .reduce((acc: { value: string; label: string }[], r: any) => {
        if (r.create_user) {
          const name = `${r.create_user.first_name} ${r.create_user.last_name}`;
          if (!seen.has(name)) { seen.add(name); acc.push({ value: name, label: name }); }
        }
        return acc;
      }, [])
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }, [incomeRow]);

  const canManageIncome = access.income_admin === true || access.income_admin === 1 ||
    access.income_manager === true || access.income_manager === 1 ||
    access.finance_enabled === true;

  const handleIncomeStatus = async (id: number, status: string) => {
    try {
      await axios.put('finance-income/update/status', { status, id: [id] });
      refreshExpenses();
    } catch {
      enqueueSnackbar('Failed to update status', { variant: 'error' });
    }
  };

  const INCOME_STATUS_COLORS: Record<string, string> = { pending: '#f59e0b', rejected: '#ef4444', paid: '#22c55e', fulfilled: '#3b82f6' };
  const INCOME_STATUS_ITEMS = [
    { value: 'pending', label: 'pending' },
    { value: 'rejected', label: 'rejected' },
    { value: 'fulfilled', label: 'approved' },
  ];
  const [incomeMenuAnchor, setIncomeMenuAnchor] = useState<null | HTMLElement>(null);
  const [incomeMenuRowId, setIncomeMenuRowId] = useState<number | null>(null);
  const handleIncomeMenuSelect = (status: string) => {
    const id = incomeMenuRowId;
    setIncomeMenuAnchor(null);
    if (id != null) handleIncomeStatus(id, status);
  };

  const incomeColumns: ColDef[] = [
    {
      key: 'date', label: t('Date'), sortable: true,
      filterType: 'daterange',
      filterExtract: (r) => r.date ?? '',
      render: (r) => r.date ? new Date(r.date).toLocaleDateString() : '—',
    },
    {
      key: 'name', label: t('Name'),
      render: (r) => r.name || '—',
    },
    {
      key: 'create_user', label: t('NameCreator'),
      filterType: 'select',
      filterOptions: incomeUserOptions,
      filterExtract: (r) => r.create_user ? `${r.create_user.first_name} ${r.create_user.last_name}` : '',
      render: (r) => r.create_user ? `${r.create_user.first_name} ${r.create_user.last_name}` : '—',
    },
    {
      key: 'description', label: t('Description'),
      render: (r) => r.description || '—',
    },
    {
      key: 'amount', label: t('Amount'), sortable: true,
      render: (r) => <span className="font-semibold text-green-600">+{Number(r.amount).toFixed(2)}</span>,
    },
    {
      key: 'status', label: t('StatusExpense'), filterType: 'select',
      filterOptions: [
        { value: 'pending', label: 'Pending' },
        { value: 'fulfilled', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
      ],
      render: (r) => {
        const color = r.status === 'pending' ? '#f59e0b'
          : r.status === 'rejected' ? '#ef4444'
          : r.status === 'paid' ? '#22c55e' : '#3b82f6';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              onClick={canManageIncome ? (e: any) => { setIncomeMenuRowId(r.id); setIncomeMenuAnchor(e.currentTarget); } : undefined}
              style={{
                fontSize: 11, fontWeight: 700, color, border: `1px solid ${color}`,
                borderRadius: 14, padding: '2px 8px', minWidth: 62, textAlign: 'center',
                textTransform: 'capitalize', cursor: canManageIncome ? 'pointer' : 'default',
              }}>
              {r.status === 'fulfilled' ? 'approved' : r.status}
            </span>
            {r.status === 'pending' && canManageIncome && (
              <>
                <button type="button" className="fx-ar-btn ok" title={t('Approve') as string}
                  onClick={() => handleIncomeStatus(r.id, 'fulfilled')}>✓</button>
                <button type="button" className="fx-ar-btn no" title={t('Reject') as string}
                  onClick={() => handleIncomeStatus(r.id, 'rejected')}>✗</button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  const shownIncome = useMemo(
    () => applySearchAndFilters(incomeRowOriginal, incomeSearch, incomeFilters, incomeColumns,
      (r: any) => [r.name, r.description, r.create_user ? `${r.create_user.first_name} ${r.create_user.last_name}` : ''].filter(Boolean).join(' ')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [incomeRowOriginal, incomeSearch, incomeFilters],
  );

  const payrollCols: ColDef[] = useMemo(() => [
    {
      key: 'pattern', label: t('HrWorkingPattern') || 'Contract', render: () => null,
      filterType: 'select',
      filterOptions: contractType.map((ct) => ({ value: String(ct.id), label: ct.name })),
      filterExtract: (u: any) => u.patternId != null ? String(u.patternId) : '',
    },
  ], [contractType]);

  const shownPayroll = useMemo(
    () => applySearchAndFilters(payrollOriginalUsers, payrollSearch, payrollFilters, payrollCols,
      (u: any) => `${u.first_name ?? ''} ${u.last_name ?? ''} ${u.second_name ?? ''}`),
    [payrollOriginalUsers, payrollSearch, payrollFilters, payrollCols],
  );

  if (!auth || !access) {
    return (
      <LoadingCenter />
    );
  }

  if (!access.finance_enabled) {
    return <NoAccess section={t('Finance')} />;
  }

  // вкладку показываем только если роль даёт просмотр этого блока —
  // иначе она открывалась пустой и сыпала 403 в консоль
  const canSee = (block: 'expenses' | 'income' | 'car_exp' | 'payroll') => {
    const view = access[`${block}_view`];
    const admin = access[`${block}_admin`];
    return view === true || view === 1 || admin === true || admin === 1;
  };

  return (
    <div className="section-page">
      <TabMenu
        value={tab}
        onChange={(_, v) => setTab(v)}
        tabs={[
          { label: t('Overview'),        value: 'overview' },
          { label: t('Expenses'),        value: 'expenses', hidden: !canSee('expenses') },
          { label: t('Income'),          value: 'income',   hidden: !canSee('income') },
          { label: t('CarExpenses') || 'Car Expenses', value: 'car', hidden: !canSee('car_exp') },
          { label: t('Payroll'),         value: 'payroll',  hidden: !canSee('payroll') },
        ]}
        rightElement={
          access.expenses_settings ? (
            <div className="flex items-center mr-[4px]">
              <Tooltip title={t('Settings')} placement="left">
                <FontAwesomeIcon
                  icon={faGear}
                  className="transition-transform duration-500 cursor-pointer opacity-60 hover:rotate-180 hover:opacity-90 text-[var(--text-color)]"
                  onClick={() => setShowEditSetting(true)}
                />
              </Tooltip>
            </div>
          ) : undefined
        }
      >
        {/* === OVERVIEW === */}
        <TabPanel value='overview' sx={{ padding: 0 }}>
          <FinanceOverview
            expenses={expensesRow}
            income={incomeRow}
            carExpenses={expensesRowCar}
            loading={loading || loadingCar || loadingIncome}
          />
        </TabPanel>

        {/* === EXPENSES === */}
        <TabPanel value='expenses' sx={{ padding: 0 }}>
          <FinanceExpensesTable
            rows={expensesRow}
            fields={fields}
            access={access}
            staticDateCompany={staticDateCompany}
            baseUrl={baseUrl}
            onAdd={() => setShowModalAdd(true)}
            onEdit={handleShow}
            refresh={refreshExpenses}
          />
        </TabPanel>

        {/* === INCOME === */}
        <TabPanel value='income' sx={{ padding: 0 }}>
          <PageToolbar>
            <SearchFilter grow columns={incomeColumns} search={incomeSearch} onSearch={setIncomeSearch} filters={incomeFilters} onFilters={setIncomeFilters} />
            {(access.income_admin === true || access.income_admin === 1 ||
              access.income_manager === true || access.income_manager === 1 ||
              access.finance_enabled === true) && (
              <button type="button" onClick={() => setShowModalIncomeAdd(true)} className="btn-primary">
                <AddIcon sx={{ fontSize: 15 }} />
                <span>{t('AddIncome')}</span>
              </button>
            )}
            <div style={{ flex: 1 }} />
            <div ref={incomeToolbarSlot} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)' }} />
          </PageToolbar>
          <div className="section-scroll">
            <DataTable
              rows={shownIncome}
              getKey={(r: any) => r.id}
              columns={incomeColumns}
              filterable={false}
              onEdit={(access.income_admin === true || access.income_admin === 1 ||
                access.income_manager === true || access.income_manager === 1 ||
                access.income_edit === true || access.income_edit === 1)
                ? (row: any) => { setSelectedIncome(row); setShowIncomeEdit(true); }
                : undefined}
              canEdit={(row: any) => row.status !== 'paid' && row.status !== 'fulfilled'}
              onDelete={access.income_admin || access.income_manager ? async (row) => {
                try {
                  await axios.delete(`finance-income/${row.id}`);
                  refreshExpenses();
                  enqueueSnackbar('Income deleted', { variant: 'success' });
                } catch {
                  enqueueSnackbar('Failed to delete income', { variant: 'error' });
                }
              } : undefined}
              defaultPageSize={20}
              toolbarRef={incomeToolbarSlot}
            />
          </div>
        </TabPanel>

        {/* === CAR EXPENSES === */}
        <TabPanel value='car' sx={{ padding: 0 }}>
          <CarExpensesSection
            rows={expensesRowCar}
            access={access}
            staticDateCompany={staticDateCompany}
            onAdd={() => setShowModalCarAdd(true)}
            onEdit={(r: any) => { setSelectedCarExpense(r); setShowCarEdit(true); }}
            refresh={refreshExpenses}
            baseUrl={baseUrl}
          />
        </TabPanel>

        {/* === PAYROLL === */}
        <TabPanel value='payroll' sx={{ padding: 0 }}>
          <PageToolbar>
            <SearchFilter grow columns={payrollCols} search={payrollSearch} onSearch={setPayrollSearch} filters={payrollFilters} onFilters={setPayrollFilters} />
            <div style={{ flex: 1 }} />
            <div ref={payrollToolbarSlot} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)' }} />
          </PageToolbar>
          <div className="section-scroll">
            {(Array.isArray(payrollUsers) && payrollUsers.length > 0) || !loadingPayroll ? (
              <PayrollTable
                access={access}
                users={shownPayroll}
                handleShow={(u: any) => { setSelectedPayrollUser(u); setShowPayrollEdit(true); }}
                contractType={contractType}
                refreshUsers={loadPayroll}
                selectedContractTypeFilter={null}
                baseUrl={baseUrl}
                toolbarRef={payrollToolbarSlot}
              />
            ) : (
              <LoadingCenter />
            )}
          </div>
        </TabPanel>
      </TabMenu>

      {/* === SIDE PANELS (edit/settings only) === */}
      <SidePanel open={showModalAdd} onClose={() => setShowModalAdd(false)}>
        <AddExpenses setShowModalAdd={setShowModalAdd} is24HourFormat={is24HourFormat} refreshExpenses={refreshExpenses} fields={fields} access={access} />
      </SidePanel>
      <SidePanel open={showModalIncomeAdd} onClose={() => setShowModalIncomeAdd(false)}>
        <AddIncome setShowModalAdd={setShowModalIncomeAdd} refreshIncome={refreshExpenses} staticDateCompany={staticDateCompany} access={access} />
      </SidePanel>
      <SidePanel open={showModalCarAdd} onClose={() => setShowModalCarAdd(false)}>
        <AddExpensesCar setShowModalAdd={setShowModalCarAdd} is24HourFormat={is24HourFormat} refreshExpenses={refreshExpenses} cars={cars} refreshCars={refreshCars} unit={unit} access={access} />
      </SidePanel>
      <SidePanel open={showCarEdit} onClose={() => setShowCarEdit(false)}>
        {selectedCarExpense && (
          <EditCarExpenses setShow={setShowCarEdit} refreshExpenses={refreshExpenses} record={selectedCarExpense} unit={unit} />
        )}
      </SidePanel>
      <SidePanel open={showIncomeEdit} onClose={() => setShowIncomeEdit(false)}>
        {selectedIncome && (
          <EditIncome setShow={setShowIncomeEdit} refreshIncome={refreshExpenses} record={selectedIncome} />
        )}
      </SidePanel>
      <Menu
        anchorEl={incomeMenuAnchor}
        open={Boolean(incomeMenuAnchor)}
        onClose={() => setIncomeMenuAnchor(null)}
        MenuListProps={{ dense: true, sx: { py: 0.5 } }}
        slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 132 } } }}
      >
        {INCOME_STATUS_ITEMS.map((s) => (
          <MenuItem
            key={s.value}
            onClick={() => handleIncomeMenuSelect(s.value)}
            sx={{ fontSize: 12.5, fontWeight: 600, minHeight: 30, py: 0.5, px: 1.5, textTransform: 'capitalize', gap: 1 }}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: INCOME_STATUS_COLORS[s.value] }} />
            {s.label}
          </MenuItem>
        ))}
      </Menu>
      <SidePanel open={showEditSetting} onClose={() => setShowEditSetting(false)} width={624}>
        <SettingsPanelBody>
          <ExpensesSettings access={access} baseUrl={baseUrl} unit={unit} refreshFields={refreshFields} />
        </SettingsPanelBody>
      </SidePanel>
      <SidePanel open={show} onClose={() => setShow(false)}>
        <EditExpenses setShow={setShow} baseUrl={baseUrl} refreshExpenses={refreshExpenses} selectedUser={selectedUser} fields={fields} />
      </SidePanel>
      <SidePanel open={showPayrollEdit} onClose={() => setShowPayrollEdit(false)}>
        {selectedPayrollUser && (
          <UserEditFinance
            selectedUser={selectedPayrollUser}
            refreshUsers={loadPayroll}
            setShow={setShowPayrollEdit}
          />
        )}
      </SidePanel>
    </div>
  );
};

export default Finance;
